import React, { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, HistogramSeries } from 'lightweight-charts';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const AdvancedStockChart = ({ symbol = 'AAPL', height = 500, onPriceUpdate, user, category, asset, currency, usdTryRate = 42.5 }) => {
  console.log('🚀 AdvancedStockChart mounted with symbol:', symbol, 'category:', category, 'currency:', currency);
  console.log('💰 Asset closing_price (dün):', asset?.closing_price);
  
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(null);
  const [previousClose, setPreviousClose] = useState(null); // Dünün kapanışı
  const [timeframe, setTimeframe] = useState('D'); // D = Daily, 60 = Hourly, 1 = 1 min
  const [countdown, setCountdown] = useState(0); // Countdown timer for next candle
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const lastCandleTimeRef = useRef(null); // Son mumun time'ını sakla
  const lastCandleDataRef = useRef(null); // Son mumun OHLC verisini sakla

  // Set closing price and initial current price immediately when component mounts
  useEffect(() => {
    if (asset?.closing_price) {
      setPreviousClose(asset.closing_price);
      console.log('✅ Previous close set from asset on mount:', asset.closing_price);
    }
    // Başlangıç fiyatını asset'ten al
    if (asset?.current_price && !currentPrice) {
      setCurrentPrice(asset.current_price);
      console.log('✅ Initial price set from asset on mount:', asset.current_price);
    }
  }, [asset?.closing_price, asset?.current_price]);

  // Real-time price polling - DEVRE DIŞI
  // NOT: Bu fonksiyon grup ayarlarını içermeyen /api/market/realtime-price endpoint'ini kullanıyordu.
  // Fiyatlar artık /api/assets/minimal'den (grup ayarlı) geliyor ve 15 saniyede bir güncelleniyor.
  // Chart sadece asset prop'undan gelen fiyatı gösteriyor.
  useEffect(() => {
    if (!symbol || !asset) return;

    // Asset'ten gelen kapanış fiyatını sakla (değişim hesabı için)
    if (asset.closing_price && asset.closing_price > 0) {
      setPreviousClose(asset.closing_price);
      console.log('💰 Asset closing_price (dün):', asset.closing_price);
    }
    
    // NOT: current_price burada KULLANILMIYOR!
    // Grafik verisi yüklendiğinde lastCandle.close kullanılacak
    // Bu sayede grafikteki fiyat ile header'daki fiyat eşleşecek
  }, [symbol, asset?.closing_price]);

  // CANLI FİYAT POLLİNG - Grafik ve header için anlık fiyat güncelleme
  useEffect(() => {
    if (!symbol) return;

    let isMounted = true;
    let pollInterval = null;
    
    // Performans optimizasyonu: KRIPTO 5 saniye, diğerleri 15 saniye
    const pollIntervalMs = category === 'KRIPTO' ? 5000 : 15000;

    const pollPrice = async () => {
      if (!isMounted) return;
      
      try {
        const response = await axios.get(`${API}/market/realtime-price/${symbol}`);
        const data = response.data;
        
        if (!isMounted) return;
        
        console.log('💰 Real-time price:', symbol, 'TL:', data.price, 'USD:', data.price_usd, 'change:', data.change_percent);
        
        // Kripto için: Grafik USD cinsinden, header'da hem USD hem TL göster
        // Diğerleri için: TL cinsinden
        let displayPrice = data.price; // Default: TL
        let chartPrice = data.price; // Grafiğe yazılacak fiyat
        
        if (category === 'KRIPTO') {
          // Kripto için USD fiyatını kullan (grafik için)
          if (data.price_usd) {
            chartPrice = data.price_usd; // Grafik USD cinsinden
            displayPrice = data.price; // Header için TL (ayrıca USD de gösterilecek)
            
            // Asset'teki current_price_usd'yi güncelle
            if (asset) {
              asset.current_price_usd = data.price_usd;
            }
          }
        }
        
        setCurrentPrice(displayPrice);
        setLastUpdate(Date.now());
        
        // Değişim yüzdesini hesapla - KAPANIŞ FİYATINDAN DİNAMİK HESAPLA
        let calculatedChange = 0;
        const closingPrice = asset?.closing_price || previousClose || 0;
        
        // Kripto için: Backend'den gelen 24h change kullan (Binance'den)
        if (category === 'KRIPTO' && data.change_percent !== undefined && data.change_percent !== null) {
          calculatedChange = data.change_percent;
          setPriceChange(calculatedChange);
        } else if (closingPrice && closingPrice > 0) {
          // Kapanış fiyatından dinamik hesapla - EN DOĞRU YÖNTEM
          calculatedChange = ((displayPrice - closingPrice) / closingPrice) * 100;
          
          // %10 SINIRI SADECE BIST_100 için geçerli
          if (category === 'BIST_100') {
            if (calculatedChange > 10) calculatedChange = 10;
            else if (calculatedChange < -10) calculatedChange = -10;
          }
          
          setPriceChange(calculatedChange);
        } else if (asset?.change_percent !== undefined && asset?.change_percent !== null && asset.change_percent !== 0) {
          // Son çare: Asset'ten gelen change_percent (0 değilse)
          calculatedChange = asset.change_percent;
          
          // %10 SINIRI SADECE BIST_100 için geçerli
          if (category === 'BIST_100') {
            if (calculatedChange > 10) calculatedChange = 10;
            else if (calculatedChange < -10) calculatedChange = -10;
          }
          
          setPriceChange(calculatedChange);
        }
        
        // Grafikteki son mumu güncelle - ANLIK
        if (candleSeriesRef.current && chartPrice) {
          // lastCandleTimeRef yoksa şu anki zamanı kullan
          let candleTime = lastCandleTimeRef.current;
          if (!candleTime) {
            const now = new Date();
            now.setHours(0, 0, 0, 0);
            candleTime = Math.floor(now.getTime() / 1000);
          }
          
          // Mevcut mumu al veya yenisini oluştur
          const currentCandle = lastCandleDataRef.current || {
            open: chartPrice,
            high: chartPrice,
            low: chartPrice,
            close: chartPrice,
          };
          
          // Mumu güncelle (high/low'u koru)
          const updatedCandle = {
            time: candleTime,
            open: currentCandle.open,
            high: Math.max(currentCandle.high, chartPrice),
            low: Math.min(currentCandle.low, chartPrice),
            close: chartPrice,
          };
          
          lastCandleDataRef.current = updatedCandle;
          
          try {
            candleSeriesRef.current.update(updatedCandle);
          } catch (e) {
            // Grafik henüz hazır değilse hata verme
          }
        }
        
        // Parent component'a fiyatı bildir (header güncellemesi için)
        if (onPriceUpdate) {
          console.log(`📊 Chart → Header: ${symbol} = ${category === 'KRIPTO' ? '$' : '₺'}${displayPrice.toFixed(2)} (${calculatedChange.toFixed(2)}%)`);
          onPriceUpdate(displayPrice, calculatedChange, data.price_usd);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Price poll error:', error);
        }
      }
    };

    // Sayfa yüklendiğinde hemen çağır
    pollPrice();

    // Periyodik güncelleme başlat
    pollInterval = setInterval(pollPrice, pollIntervalMs);
    
    console.log(`🔄 Price polling started for ${symbol}: every ${pollIntervalMs/1000}s`);
    
    // Cleanup
    return () => {
      isMounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
        console.log(`🛑 Price polling stopped for ${symbol}`);
      }
    };
  }, [symbol, onPriceUpdate, category, asset?.closing_price]);

  // Countdown timer for next candle
  useEffect(() => {
    const updateCountdown = () => {
      const now = Date.now();
      let intervalMs;

      switch (timeframe) {
        case '1': intervalMs = 60 * 1000; break; // 1 min
        case '5': intervalMs = 5 * 60 * 1000; break; // 5 min
        case '15': intervalMs = 15 * 60 * 1000; break; // 15 min
        case '60': intervalMs = 60 * 60 * 1000; break; // 1 hour
        case 'D': intervalMs = 24 * 60 * 60 * 1000; break; // 1 day
        default: intervalMs = 60 * 1000;
      }

      const elapsed = now % intervalMs;
      const remaining = Math.ceil((intervalMs - elapsed) / 1000);
      setCountdown(remaining);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [timeframe]);

  useEffect(() => {
    console.log('📊 useEffect triggered - symbol:', symbol, 'timeframe:', timeframe);
    
    // Check if container is mounted
    if (!chartContainerRef.current) {
      console.log('⚠️ Container ref not ready yet');
      return;
    }
    
    console.log('✅ Container ref mounted, creating chart...');

    // Create chart - Profesyonel Stil
    const chart = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: height,
      layout: {
        background: { color: '#131722' }, // Koyu tema
        textColor: '#D9D9D9', // Açık metin
        fontSize: 12,
      },
      grid: {
        vertLines: { 
          color: 'rgba(42, 46, 57, 0.5)', 
          style: 1, // Noktalı çizgi
          visible: true,
        },
        horzLines: { 
          color: 'rgba(42, 46, 57, 0.5)', 
          style: 1, // Noktalı çizgi
          visible: true,
        },
      },
      crosshair: {
        mode: 1, // Normal crosshair
        vertLine: {
          color: '#758696',
          width: 1,
          style: 3, // Kesikli
          labelBackgroundColor: '#4c525e',
        },
        horzLine: {
          color: '#758696',
          width: 1,
          style: 3, // Kesikli
          labelBackgroundColor: '#4c525e',
        },
      },
      rightPriceScale: {
        borderColor: '#2a2e39',
        visible: true,
        autoScale: true, // AutoScale açık
        scaleMargins: {
          top: 0.15, // Üstte %15 boşluk
          bottom: 0.1, // Altta %10 boşluk
        },
        borderVisible: true,
        entireTextOnly: false,
        ticksVisible: true,
        mode: 0, // Normal mod
      },
      localization: {
        locale: (category === 'KRIPTO' || category === 'EMTIA' || currency === 'USD') ? 'en-US' : 'tr-TR',
        dateFormat: 'dd MMM yyyy',
        priceFormatter: (price) => {
          // Kripto, Emtia veya USD para birimi için dolar formatı
          if (category === 'KRIPTO' || category === 'EMTIA' || currency === 'USD') {
            return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          }
          // Diğerleri için TRY formatı
          return '₺' + price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
      },
      leftPriceScale: {
        visible: false,
      },
      timeScale: {
        borderColor: '#2B2B43',
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 12,
        barSpacing: 6,
        fixLeftEdge: false,
        fixRightEdge: false,
        lockVisibleTimeRangeOnResize: true,
        rightBarStaysOnScroll: true,
        borderVisible: true,
        visible: true,
      },
      watermark: {
        visible: false,
      },
    });

    // Add candlestick series (v5 API) - Profesyonel Stili
    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a', // Yeşil (yükseliş)
      downColor: '#ef5350', // Kırmızı (düşüş)
      borderVisible: true, // Kenar çizgisi göster
      borderUpColor: '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor: '#26a69a', // Fitil rengi (üst)
      wickDownColor: '#ef5350', // Fitil rengi (alt)
      wickVisible: true, // Fitiller görünsün
    });

    // Volume series removed - user doesn't want volume bars
    
    console.log('✅ Chart series added (no volume)');

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;

    // Handle resize
    const handleResize = () => {
      if (chartContainerRef.current) {
        chart.applyOptions({
          width: chartContainerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener('resize', handleResize);

    // Fetch data SADECE BİR KERE (symbol veya timeframe değişince)
    fetchChartData();

    // Cleanup
    return () => {
      console.log('🧹 Cleaning up chart for', symbol);
      window.removeEventListener('resize', handleResize);
      
      // Clear refs first
      candleSeriesRef.current = null;
      lastCandleTimeRef.current = null;
      lastCandleDataRef.current = null;
      
      // Remove chart
      if (chartRef.current) {
        try {
          chartRef.current.remove();
        } catch (e) {
          console.warn('Chart cleanup warning:', e.message);
        }
        chartRef.current = null;
      }
    };
  }, [symbol, timeframe, category]);

  // Real-time candle update (for intraday timeframes)
  useEffect(() => {
    if (!candleSeriesRef.current || !['1', '5', '15', '60'].includes(timeframe)) return;

    let isMounted = true;
    let updateInterval = null;

    const updateLastCandle = async () => {
      if (!isMounted || !candleSeriesRef.current) return;
      
      try {
        const apiSymbol = ['BTC', 'ETH', 'BNB'].includes(symbol) ? `${symbol}/USD` : symbol;
        const encodedSymbol = encodeURIComponent(apiSymbol);
        
        // Fetch latest candles
        const toTimestamp = Math.floor(Date.now() / 1000);
        const fromTimestamp = toTimestamp - (24 * 60 * 60); // Last 24 hours
        
        const response = await axios.get(
          `${API}/market/candles/${encodedSymbol}?resolution=${timeframe}&from_timestamp=${fromTimestamp}&to_timestamp=${toTimestamp}`
        );
        
        if (!isMounted || !candleSeriesRef.current) return;
        
        const data = response.data;
        if (data.s === 'ok' && data.t && data.t.length > 0) {
          // Update only the last candle
          const lastIndex = data.t.length - 1;
          const lastCandle = {
            time: data.t[lastIndex],
            open: data.o[lastIndex],
            high: data.h[lastIndex],
            low: data.l[lastIndex],
            close: data.c[lastIndex],
          };
          
          candleSeriesRef.current.update(lastCandle);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Real-time candle update error:', error);
        }
      }
    };

    // Update every 5 seconds for intraday charts
    updateInterval = setInterval(updateLastCandle, 5000);
    
    // Cleanup
    return () => {
      isMounted = false;
      if (updateInterval) {
        clearInterval(updateInterval);
      }
    };
  }, [symbol, timeframe]);

  const fetchChartData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Map timeframe to interval
      const intervalMap = {
        '1': '1m',
        '5': '5m',
        '15': '15m',
        '60': '1h',
        'D': '1d',
        'W': '1w',
        'M': '1mo'
      };
      const interval = intervalMap[timeframe] || '1h';

      // EMTIA (Commodities) and ENDEKSLER (Indices)
      if (category === 'EMTIA' || category === 'ENDEKSLER') {
        const assetType = category === 'EMTIA' ? 'commodity' : 'index';
        console.log(`📊 Fetching ${assetType} chart data for:`, symbol, 'interval:', interval);
        
        // Get auth token from localStorage
        const token = localStorage.getItem('token');
        const chartResponse = await axios.get(`${API}/chart-data/${symbol}?interval=${interval}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        const chartResult = chartResponse.data;
        
        console.log(`📊 ${assetType.charAt(0).toUpperCase() + assetType.slice(1)} chart data received:`, chartResult.status, chartResult.data?.length, 'candles');
        
        if (chartResult.status === 'success' && chartResult.data && chartResult.data.length > 0) {
          // Convert to chart format
          let chartData = chartResult.data.map((candle) => ({
            time: candle.timestamp,
            open: candle.open,
            high: candle.high,
            low: candle.low,
            close: candle.close,
          }));
          
          // DÜZELT: Zaman sırasına göre sırala ve duplicate'leri temizle
          chartData = chartData
            .sort((a, b) => a.time - b.time) // Artan sırada sırala
            .filter((candle, index, array) => {
              // İlk eleman veya bir öncekinden farklı time
              return index === 0 || candle.time !== array[index - 1].time;
            });
          
          console.log(`✅ ${assetType} chart data cleaned: ${chartData.length} candles (sorted & deduplicated)`);
          
          candleSeriesRef.current.setData(chartData);
          
          // Save last candle for real-time updates
          const lastCandle = chartData[chartData.length - 1];
          lastCandleTimeRef.current = lastCandle.time;
          lastCandleDataRef.current = {
            open: lastCandle.open,
            high: lastCandle.high,
            low: lastCandle.low,
            close: lastCandle.close
          };
          
          // Set previous close from backend
          if (asset?.closing_price) {
            setPreviousClose(asset.closing_price);
            console.log(`💰 ${assetType.charAt(0).toUpperCase() + assetType.slice(1)} previous close (dün) - BACKEND:`, asset.closing_price);
          } else if (chartData.length >= 2) {
            const previousCandle = chartData[chartData.length - 2];
            setPreviousClose(previousCandle.close);
            console.log(`💰 ${assetType.charAt(0).toUpperCase() + assetType.slice(1)} previous close (dün) - CHART:`, previousCandle.close);
          }
          
          // Set current price from last candle - GRAFİKTEKİ SON FİYAT
          setCurrentPrice(lastCandle.close);
          
          // Calculate price change
          const closingPrice = asset?.closing_price || previousClose || 0;
          let calculatedChange = 0;
          if (closingPrice > 0) {
            calculatedChange = ((lastCandle.close - closingPrice) / closingPrice) * 100;
          }
          setPriceChange(calculatedChange);
          
          // Notify parent component - GRAFİKTEKİ FİYATI GÖNDER
          if (onPriceUpdate) {
            console.log(`🔄 Chart → Parent: ${symbol} = ₺${lastCandle.close.toFixed(2)} (${calculatedChange.toFixed(2)}%)`);
            onPriceUpdate(lastCandle.close, calculatedChange);
          }
          
          // Fit content
          chartRef.current.timeScale().fitContent();
          
          console.log(`✅ ${assetType.charAt(0).toUpperCase() + assetType.slice(1)} chart rendered with`, chartData.length, 'candles');
        } else {
          console.error(`❌ No ${assetType} chart data received or error status`);
          setError('Grafik verisi bulunamadı');
        }
        
        return; // Exit early for commodities and indices
      }

      // KRIPTO ve diğer varlıklar
      console.log('📊 Fetching chart data for:', symbol, 'timeframe:', timeframe);

      // NOT: Fiyat burada ALMIYOR! pollPrice fonksiyonu gerçek fiyatı günceller

      // Get klines (candle data) - 10 saniye timeout
      const klinesUrl = `${API}/binance/klines/${symbol}?interval=${interval}&limit=100`;
      console.log('📈 Fetching klines:', klinesUrl);

      const klinesResponse = await axios.get(klinesUrl, { timeout: 10000 });
      const data = klinesResponse.data;
      
      console.log('📊 Klines data received:', data.klines?.length, 'candles');

      if (data.klines && data.klines.length > 0) {
        // Prepare candlestick data - Backend zaten doğru currency'de
        let chartData = data.klines.map((kline) => ({
          time: Math.floor(kline.openTime / 1000),
          open: kline.open,
          high: kline.high,
          low: kline.low,
          close: kline.close,
        }));

        // DÜZELT: Zaman sırasına göre sırala ve duplicate'leri temizle
        chartData = chartData
          .sort((a, b) => a.time - b.time) // Artan sırada sırala
          .filter((candle, index, array) => {
            // İlk eleman veya bir öncekinden farklı time
            return index === 0 || candle.time !== array[index - 1].time;
          });

        console.log(`✅ Chart data cleaned: ${chartData.length} candles (sorted & deduplicated)`);

        // Volume data removed - user doesn't want volume bars

        candleSeriesRef.current.setData(chartData);

        // Son mumun time ve data'sını sakla (real-time update için)
        const lastCandle = chartData[chartData.length - 1];
        lastCandleTimeRef.current = lastCandle.time;
        lastCandleDataRef.current = {
          open: lastCandle.open,
          high: lastCandle.high,
          low: lastCandle.low,
          close: lastCandle.close
        };
        
        // GRAFİKTEKİ SON FİYATI KULLAN - HEADER İLE UYUMLU OLMASI İÇİN
        setCurrentPrice(lastCandle.close);
        
        // DÜNÜN KAPANIŞINI AL - Backend'den gelen closing_price kullan (DOĞRU!)
        const closingPrice = asset?.closing_price || (chartData.length >= 2 ? chartData[chartData.length - 2].close : 0);
        if (closingPrice > 0) {
          setPreviousClose(closingPrice);
          console.log('💰 Previous close (dün):', closingPrice);
        }
        
        // Calculate price change and notify parent
        let calculatedChange = 0;
        if (closingPrice > 0) {
          calculatedChange = ((lastCandle.close - closingPrice) / closingPrice) * 100;
          setPriceChange(calculatedChange);
        }
        
        // KRİPTO İÇİN KRİTİK: Grafikteki fiyat USD cinsinden!
        // lastCandle.close = USD fiyatı (örn: $95,000)
        // Header'da gösterilecek: $95,000 + TL karşılığı
        const usdPrice = lastCandle.close; // Grafikteki fiyat zaten USD
        
        // Notify parent component - GRAFİKTEKİ FİYATI GÖNDER (KRİPTO için USD)
        if (onPriceUpdate) {
          console.log(`🔄 Chart → Parent (KRIPTO): ${symbol} = $${usdPrice.toFixed(2)} (${calculatedChange.toFixed(2)}%)`);
          // KRİPTO için: price=USD fiyatı, priceUsd=USD fiyatı
          onPriceUpdate(usdPrice, calculatedChange, usdPrice);
        }
        
        console.log('📍 Last candle saved:', lastCandle.time, lastCandle.close, 'USD');

        // Fit content
        chartRef.current.timeScale().fitContent();
        
        console.log('✅ Chart rendered with', chartData.length, 'candles');
      } else {
        console.error('❌ No klines data received');
        setError('Grafik verisi bulunamadı');
      }
    } catch (err) {
      console.error('❌ Chart data fetch error:', err);
      setError(err.response?.data?.detail || 'Grafik verileri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const timeframes = [
    { label: '1D', value: 'D' },
    { label: '1H', value: '60' },
    { label: '15M', value: '15' },
    { label: '5M', value: '5' },
    { label: '1M', value: '1' },
  ];

  return (
    <div className="bg-gray-800 rounded-lg p-4 shadow-xl">
      {/* Timeframe Selector - Kompakt, fiyat TradingPageNew header'da gösteriliyor */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-400">{symbol} Grafik</div>
        <div className="flex gap-1 sm:gap-2 items-center justify-center flex-wrap">
          {timeframes.map((tf) => (
            <button
              key={tf.value}
              onClick={() => setTimeframe(tf.value)}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded font-semibold text-xs sm:text-sm transition-colors ${
                timeframe === tf.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
              }`}
            >
              {tf.label}
            </button>
          ))}
          
          {/* Countdown Timer - Show only for intraday timeframes */}
          {countdown > 0 && ['1', '5', '15', '60'].includes(timeframe) && (
            <div className="ml-4 px-4 py-2 bg-red-600 rounded text-lg font-bold font-mono text-white shadow-lg">
              {Math.floor(countdown / 60).toString().padStart(2, '0')}:{(countdown % 60).toString().padStart(2, '0')}
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      {loading && (
        <div className="flex items-center justify-center" style={{ height: height }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Grafik yükleniyor...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center justify-center" style={{ height: height }}>
          <div className="text-center">
            <p className="text-yellow-400 mb-2">⚠️ Grafik verisi geçici olarak alınamıyor</p>
            <p className="text-gray-500 text-sm mb-3">Fiyat bilgisi güncel, grafik birazdan yüklenecek</p>
            <button
              onClick={fetchChartData}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      )}

      {/* Always render container, even while loading */}
      <div ref={chartContainerRef} style={{ position: 'relative', height: height }} />

      {/* Legend */}
      <div className="mt-3 flex gap-6 text-sm text-gray-400">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-500 rounded"></div>
          <span>Yükseliş</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-500 rounded"></div>
          <span>Düşüş</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-gray-500 rounded"></div>
          <span>Hacim</span>
        </div>
      </div>
    </div>
  );
};

export default AdvancedStockChart;
